import React, { useState } from "react";
import { observer } from "mobx-react";
import { ContactsCSVReader, RoundedButton, SuccessfulModal } from "../../../../../elements";
import { HeaderContainer, ErrorSubTitles, ErrorList, ButtonsContainer } from "./SpreadsheetContactsMapping.styled";
import { useStore } from "../../../../../hooks";
import { useContactValidation } from "./hooks/useContactValidation";
import { MarketingErrorMessages, VALIDATION_MESSAGES } from "../../../../../utils/enums";
import Alerts from "../../../../../elements/v2/Alerts/Alerts";

const SpreadsheetContactsMapping = observer(({ showSendButton = false, onFileUpload }) => {
  const [isSaved, setIsSaved] = useState(false);
  const { contactSegmentStore, authStore } = useStore();

  const {
    validationErrors,
    setValidationErrors,
    validateFields,
    clearValidationErrors,
    removeDuplicateEmails,
    filteredMappedFieldsGlobal
  } = useContactValidation(contactSegmentStore);

  const [showAlert, setShowAlert] = useState(false);

  const onRemoveDuplicated = () => {
    removeDuplicateEmails();
  };

  const onCancelUpload = () => {
    handleCloseValidationModal();
    contactSegmentStore.clear();
  };

  const areRequiredFieldsMapped = () => {
    const { finalFixedMappings, defaultFileFields, fileUploadedHeaders } = contactSegmentStore;
    
    const isEmailMappedInternally = finalFixedMappings.email?.trim() !== "";
    const isFirstNameMappedInternally = finalFixedMappings.firstname?.trim() !== "";

    const emailFieldSelected = defaultFileFields.find(field => field.mapping === "email");
    const firstNameFieldSelected = defaultFileFields.find(field => field.mapping === "firstname");
  
    const headers = fileUploadedHeaders.map(header => header.value.toLowerCase());
    const isEmailInHeaders = emailFieldSelected && headers.includes(emailFieldSelected.value.toLowerCase());
    const isFirstNameInHeaders = firstNameFieldSelected && headers.includes(firstNameFieldSelected.value.toLowerCase());

    if (!isEmailMappedInternally || !isFirstNameMappedInternally) {
      return false;
    }
  
    if (!emailFieldSelected || !firstNameFieldSelected) {
      return false;
    }
  
    if (!isEmailInHeaders || !isFirstNameInHeaders) {
      return false;
    }

    if (!filteredMappedFieldsGlobal.email || !filteredMappedFieldsGlobal.first_name) {
      return false;
    }

    return true;
  };

  const formatErrorsToHTML = (errors) => {
    if (!errors.length) return null;

    const groupedErrors = errors.reduce((acc, error) => {
      if (typeof error === 'string') {
        acc.generic.push(error);
      } else {
        if (!acc[error.type]) acc[error.type] = [];
        acc[error.type].push(error.fields);
      }
      return acc;
    }, { generic: [] });

    const thereAreDuplicatedEmails = groupedErrors.duplicated_emails && groupedErrors.duplicated_emails.length > 0;
    const thereAreInvalidFields = groupedErrors.invalid && groupedErrors.invalid.length > 0;

    const isOnlyDuplicatedEmailsError =
      groupedErrors.duplicated_emails &&
      groupedErrors.duplicated_emails.length > 0

    return (
      <ErrorList role="alert" aria-live="polite">
        {groupedErrors.generic.length > 0 && (
          <li>
            <ErrorSubTitles>{groupedErrors.generic.join(", ")}</ErrorSubTitles>
          </li>
        )}
        {thereAreDuplicatedEmails && (
          <li>
            <ErrorSubTitles>
              The following email fields are duplicated:
            </ErrorSubTitles>
            <ul>
              {Array.from(new Set(groupedErrors.duplicated_emails.flat())).map((email, i) => (
                <li key={i}>{email}</li>
              ))}
            </ul>
          </li>
        )}
        {groupedErrors.invalid && groupedErrors.invalid.length > 0 && (
          <li>
            <ErrorSubTitles>The following fields contain invalid data:</ErrorSubTitles>
            <ul aria-label="Invalid fields">
              {Array.from(new Set(groupedErrors.invalid.flatMap(fields =>
                fields.map(field => `${field.name}: ${field.error}`)
              ))).map((errorText, i) => (
                <li key={i}>{errorText}</li>
              ))}
            </ul>
          </li>
        )}
        <ButtonsContainer>
          {!areRequiredFieldsMapped() || isOnlyDuplicatedEmailsError && !thereAreInvalidFields && (
            <RoundedButton onClick={onRemoveDuplicated} width="200px">
              Remove Duplicates Emails
            </RoundedButton>
          )}
          {areRequiredFieldsMapped() && (
            <RoundedButton onClick={onCancelUpload} width="200px">
              Cancel upload
            </RoundedButton>
          )}
        </ButtonsContainer>
      </ErrorList>
    );
  };

  const onSave = () => {
    const errors = validateFields();
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    if (!isSaved) {
      contactSegmentStore.setHeaderMapping();
      setIsSaved(true);
    }
  };

  const onSend = async () => {
    try {
      await contactSegmentStore.uploadFileToS3();
      onFileUpload?.();
    } catch (error) {
      setValidationErrors([VALIDATION_MESSAGES.UPLOAD_ERROR(error.message)]);
      setShowAlert(true);
    }
  };

  const handleCloseValidationModal = () => {
    clearValidationErrors();
  };

  return (
    <>
      {showAlert && (
        <Alerts
          severity={"error"}
          width={"auto"}
          top={"61px"}
          right={"16px"}
          message={MarketingErrorMessages.SEGMENT_CONTACT_DATA.message}
          onClose={() => setShowAlert(false)}
        />
      )}

      <HeaderContainer role="banner">
        <b>Make sure your files are ready to upload before importing</b>

        {!isSaved && (
          <RoundedButton
            onClick={onSave}
            width="200px"
            disabled={!contactSegmentStore?.parsedContactList.length}
            aria-label="save contact mapping"
          >
            Save
          </RoundedButton>
        )}

        {showSendButton && isSaved && (
          <RoundedButton
            onClick={onSend}
            width="200px"
            disabled={!contactSegmentStore?.parsedContactList.length}
            aria-label="Send contact data"
          >
            Send
          </RoundedButton>
        )}
      </HeaderContainer>

      <ContactsCSVReader
        isSaved={isSaved}
        setIsSaved={setIsSaved}
        aria-label="CSV file reader"
      />

      <SuccessfulModal
        isOpen={validationErrors.length > 0}
        onClose={handleCloseValidationModal}
        title={VALIDATION_MESSAGES.VALIDATION_MODAL.TITLE}
        subtitle={VALIDATION_MESSAGES.VALIDATION_MODAL.SUBTITLE}
        subtitle2={formatErrorsToHTML(validationErrors)}
        height="auto"
        width="500px"
        zIndex={true}
        role="dialog"
        aria-modal="true"
        aria-labelledby="validation-modal-title"
      />
    </>
  );
});

export default SpreadsheetContactsMapping;